
import { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { UserProfile } from "@/types/user";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useDashboardData } from "@/components/dashboard/useDashboardData";

interface TeamCheckinModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSend?: (data: TeamCheckinData) => void;
}

export interface TeamCheckinData {
  recipients: UserProfile[];
  message: string;
}

const TeamCheckinModal = ({ isOpen, onOpenChange, onSend }: TeamCheckinModalProps) => {
  const { toast } = useToast();
  const { teamMembers } = useDashboardData();
  const [message, setMessage] = useState("Hey! How's your week going? Just checking in.");
  const [selectedMembers, setSelectedMembers] = useState<UserProfile[]>([]);

  useEffect(() => {
    // Pre-select all team members when modal opens
    if (isOpen) {
      setSelectedMembers(teamMembers);
    }
  }, [isOpen, teamMembers]);

  const handleMemberToggle = (member: UserProfile) => {
    if (selectedMembers.some(m => m.id === member.id)) {
      setSelectedMembers(selectedMembers.filter(m => m.id !== member.id));
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  const handleSubmit = () => {
    if (selectedMembers.length === 0) {
      toast({
        title: "No recipients selected",
        description: "Please select at least one team member",
        variant: "destructive"
      });
      return;
    }

    if (!message.trim()) {
      toast({
        title: "Message is empty",
        description: "Please enter a message",
        variant: "destructive"
      });
      return;
    }

    const data: TeamCheckinData = {
      recipients: selectedMembers,
      message
    };

    if (onSend) {
      onSend(data);
    }
    
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Team Check-in</DialogTitle>
          <DialogDescription>
            Send a check-in message to your team members.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div>
            <Label className="mb-2 block">Select Recipients</Label>
            <div className="max-h-[200px] overflow-y-auto border rounded-md p-2">
              {teamMembers.map(member => (
                <div key={member.id} className="flex items-center space-x-2 py-1">
                  <Checkbox 
                    id={member.id} 
                    checked={selectedMembers.some(m => m.id === member.id)}
                    onCheckedChange={() => handleMemberToggle(member)}
                  />
                  <Label htmlFor={member.id} className="font-normal cursor-pointer">
                    {member.name} {member.role && `(${member.role})`}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <Label htmlFor="message" className="mb-2 block">
              Message
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit}>Send Check-in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeamCheckinModal;
